// CTSK-525 — one card in the Masonry / Grid document wall.
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import type { DocumentListItem } from '@captain-tasko/shared';
import { MimeIcon } from '../MimeIcon.js';
import { formatBytes } from '../../lib/format.js';
import { apiSingle } from '../../lib/api.js';
import { DocumentPreview } from './DocumentPreview.js';
import { HOVER_ZOOM_SCALE, loadHoverZoom, type HighlightEffect } from './viewPrefs.js';

export const HOVER_FOCUS_MS = 1000;
const PRELOAD_MARGIN = '500px';

/** True once the element has come within PRELOAD_MARGIN of the viewport (sticky). jsdom: always true. */
function useNearViewport(): [(el: HTMLElement | null) => void, boolean] {
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  const ioRef = useRef<IntersectionObserver | null>(null);
  const attach = useCallback((el: HTMLElement | null) => {
    ioRef.current?.disconnect();
    ioRef.current = null;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); }
    }, { rootMargin: PRELOAD_MARGIN });
    io.observe(el);
    ioRef.current = io;
  }, []);
  // No unmount-cleanup effect: StrictMode's simulated unmount would disconnect the observer
  // and React 18 doesn't re-run the ref callback, so cards would never load. React calls
  // attach(null) on real unmount, which disconnects.
  return [attach, near];
}

/** Search hits carry no mime/size; fetch the document row only when its preview opens. */
function DetailPreview({ doc, scaled }: { doc: DocumentListItem; scaled: boolean }) {
  const q = useQuery({
    queryKey: ['document-card-detail', doc.id],
    queryFn: () => apiSingle<DocumentListItem>(`/documents/${doc.id}`),
    staleTime: 5 * 60_000,
  });
  if (q.isError) return <p className="doccard-note" role="alert">Preview unavailable.</p>;
  if (!q.data) return <p className="doccard-note">Loading…</p>;
  return <DocumentPreview doc={q.data} scaled={scaled} />;
}

export interface CardActions {
  onWatch: (doc: DocumentListItem) => void;
  onPipeline: (doc: DocumentListItem) => void;
  onCopyLink: (doc: DocumentListItem) => void;
  onFindMore: (doc: DocumentListItem) => void;
  onTimelane: (doc: DocumentListItem) => void;
  onPostNote: (doc: DocumentListItem) => void;
  onPostComment: (doc: DocumentListItem) => void;
}

export function DocumentCard({
  doc, expanded, onToggle, onOpen, extras, pinned = false, scaled = false, actions, highlight = null, snippet, needsDetail = false, metaOverride, selectable = false, selected = false, onSelect,
}: {
  doc: DocumentListItem;
  expanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
  extras?: ReactNode;
  /** Preview-all mode: always expanded, no per-card toggle. */
  pinned?: boolean;
  scaled?: boolean;
  actions?: CardActions;
  /** Effect to play while this card is the one the user just came back from. */
  highlight?: HighlightEffect | null;
  /** Search hits: always-visible highlighted snippet under the chips. */
  snippet?: ReactNode;
  /** Mime/size unknown (search hit): resolve via GET /documents/:id when the preview opens. */
  needsDetail?: boolean;
  metaOverride?: ReactNode;
  /** Bulk-select mode: show a checkbox; clicking the card toggles it instead of opening. */
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const [nearRef, near] = useNearViewport();
  const [zoom] = useState(() => HOVER_ZOOM_SCALE[loadHoverZoom()]);
  const bodyId = `doccard-body-${doc.id}`;
  const [focused, setFocused] = useState(false);
  const [menu, setMenu] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  function enter() {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setFocused(true), HOVER_FOCUS_MS);
  }
  function leave() {
    if (timer.current) clearTimeout(timer.current);
    setFocused(false);
    setMenu(false);
    setPostOpen(false);
  }
  function onKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (e.key === 'Escape') {
      if (menu) { e.stopPropagation(); setMenu(false); return; }
      if (expanded && !pinned) { e.stopPropagation(); onToggle(); }
    }
  }
  // Clicking the preview / empty card area opens the document; real controls keep their own action.
  function onCardClick(e: MouseEvent<HTMLElement>) {
    if ((e.target as HTMLElement).closest('button, a, input, select')) return;
    if (selectable) { onSelect?.(); return; }
    onOpen();
  }
  const stop = (fn: () => void) => (e: MouseEvent) => { e.stopPropagation(); fn(); };

  return (
    <article
      className={`doccard${focused ? ' doccard--focus' : ''}`}
      data-expanded={expanded ? 'true' : 'false'}
      data-highlight={highlight && highlight !== 'off' ? highlight : undefined}
      data-doc-id={doc.id}
      data-selected={selectable ? (selected ? 'true' : 'false') : undefined}
      style={{ ['--doccard-zoom' as string]: zoom }}
      onKeyDown={onKeyDown}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onClick={onCardClick}
      ref={nearRef}
    >
      {focused && actions && !selectable && (
        <div className="doccard-chips" role="group" aria-label={`Actions for ${doc.title}`}>
          <button type="button" className="doccard-chip doccard-chip--a" title="Open document" onClick={stop(onOpen)}>A · Open</button>
          <button type="button" className="doccard-chip doccard-chip--b" title={doc.isWatchedByMe ? 'Stop watching' : 'Watch'} onClick={stop(() => actions.onWatch(doc))}>
            B · {doc.isWatchedByMe ? 'Unwatch' : 'Watch'}
          </button>
          <button type="button" className="doccard-chip doccard-chip--c" title="Open pipeline output" onClick={stop(() => actions.onPipeline(doc))}>C · Pipeline</button>
          <span className="doccard-chip-wrap">
            <button type="button" className="doccard-chip doccard-chip--d" aria-expanded={menu} onClick={stop(() => setMenu((m) => !m))}>D · More</button>
            {menu && (
              <div className="doccard-menu" role="menu">
                <a role="menuitem" href={`/api/v1/documents/${doc.id}/content`} download onClick={(e) => e.stopPropagation()}>Download</a>
                <button role="menuitem" type="button" onClick={stop(() => { actions.onCopyLink(doc); setMenu(false); })}>Permalink</button>
                <button role="menuitem" type="button" onClick={stop(() => { actions.onFindMore(doc); setMenu(false); })}>Find more</button>
                <button role="menuitem" type="button" onClick={stop(() => { actions.onTimelane(doc); setMenu(false); })}>Timelane</button>
                <span className="doccard-sub-wrap">
                  <button role="menuitem" type="button" aria-haspopup="menu" aria-expanded={postOpen} onClick={stop(() => setPostOpen((o) => !o))}>◂ Post</button>
                  {postOpen && (
                    <div className="doccard-menu doccard-submenu" role="menu" aria-label="Post">
                      <button role="menuitem" type="button" onClick={stop(() => { actions.onPostNote(doc); setMenu(false); setPostOpen(false); })}>Note</button>
                      <button role="menuitem" type="button" onClick={stop(() => { actions.onPostComment(doc); setMenu(false); setPostOpen(false); })}>Comment</button>
                      <button role="menuitem" type="button" disabled title="Chat posting isn't available yet">Chat (soon)</button>
                    </div>
                  )}
                </span>
              </div>
            )}
          </span>
        </div>
      )}
      <header className="doccard-head">
        {selectable && (
          <input
            type="checkbox"
            className="doccard-select"
            checked={selected}
            onChange={() => onSelect?.()}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${doc.title}`}
          />
        )}
        <MimeIcon mime={doc.mimeType} size={18} />
        <button type="button" className="doccard-title" onClick={selectable ? () => onSelect?.() : onOpen} title={doc.title}>{doc.title}</button>
        {!pinned && <button
          type="button"
          className="btn btn--ghost doccard-toggle"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          {expanded ? 'Hide' : 'Preview'}
        </button>}
      </header>
      <div className="doccard-meta">
        {metaOverride ?? <>{formatBytes(doc.sizeBytes)} · {new Date(doc.createdAt).toLocaleDateString()}</>}
      </div>
      {extras && <div className="doccard-extras">{extras}</div>}
      {snippet && <p className="doccard-snippet">{snippet}</p>}
      {expanded && (
        <div className="doccard-body" id={bodyId}>
          {near
            ? (needsDetail ? <DetailPreview doc={doc} scaled={scaled} /> : <DocumentPreview doc={doc} scaled={scaled} />)
            : <div className="doccard-placeholder" aria-hidden="true" />}
        </div>
      )}
    </article>
  );
}
